import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, FileCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dosage, setDosage] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setDosage(r.data.dosages[0]);
        setQty(1);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!product || !["sfh-3", "sfh-tz"].includes(product.id)) return;
    const tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    tag.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [product]);

  if (notFound) {
    return (
      <main data-testid="product-not-found" className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 pt-24">
        <p className="font-display text-4xl text-[#A3A3A3]">Compound not found</p>
        <Link to="/products" data-testid="back-to-catalog-link" className="font-mono text-xs tracking-[0.2em] text-[#00ff72]">
          &#8592; BACK TO CATALOG
        </Link>
      </main>
    );
  }

  if (!product) {
    return <main className="min-h-screen pt-24" />;
  }

  const handleAdd = () => {
    addItem(product, dosage, qty);
    toast.success(`${product.name} (${dosage.label}) x${qty} added to cart`);
  };

  return (
    <main className="pt-24">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <Link
          to="/products"
          data-testid="back-to-catalog-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#A3A3A3] transition-colors hover:text-[#00ff72]"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          BACK TO CATALOG
        </Link>

        <div className="mt-10 grid gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="border border-white/10"
          >
            <div className="group relative aspect-[4/3] overflow-hidden bg-black">
              <img
                src={product.image}
                alt={product.name}
                className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 group-hover:scale-105 group-hover:grayscale-0"
              />
              <span className="absolute left-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-[#00ff72] backdrop-blur-sm">
                {product.category.toUpperCase()}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-white/10 px-4 py-3">
              <span className="font-mono text-[10px] tracking-widest text-[#666666]">CAS {product.cas}</span>
              <span className="flex items-center gap-1 font-mono text-[10px] tracking-widest text-[#A3A3A3]">
                <FileCheck className="h-3 w-3 text-[#00ff72]" /> COA AVAILABLE UPON REQUEST
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
          >
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">
              {product.category.toUpperCase()} / RESEARCH GRADE
            </p>
            <h1 data-testid="product-name" className="mt-3 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-6xl">
              {product.name}
            </h1>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-[#A3A3A3]">{product.description}</p>

            <div className="mt-8 space-y-3 border border-white/10 p-5 font-mono text-xs">
              {!product.external_url && (dosage?.purity ?? product.purity) != null && (
                <div className="flex justify-between gap-4">
                  <span className="tracking-widest text-[#666666]">PURITY</span>
                  <span data-testid="product-purity" className="text-[#00ff72]">{(dosage?.purity ?? product.purity)}% HPLC</span>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <span className="shrink-0 tracking-widest text-[#666666]">SEQUENCE</span>
                <span className="break-all text-right text-[#A3A3A3]">{product.sequence}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="tracking-widest text-[#666666]">CAS NO.</span>
                <span className="text-[#A3A3A3]">{product.cas}</span>
              </div>
              {!product.external_url && (
                <div className="flex justify-between gap-4">
                  <span className="tracking-widest text-[#666666]">STORAGE</span>
                  <span className="text-[#A3A3A3]">-20&deg;C, LYOPHILIZED</span>
                </div>
              )}
            </div>

            {product.external_url ? (
              <a
                href={product.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shop-external-btn"
                className="mt-8 flex items-center justify-center gap-3 bg-[#00ff72] px-8 py-5 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"
              >
                <ExternalLink className="h-4 w-4" />
                SHOP AT OUR OFFICIAL STORE
              </a>
            ) : (
              <>
                <div className="mt-8">
                  <p className="mb-3 font-mono text-[10px] tracking-[0.3em] text-[#666666]">SELECT DOSAGE</p>
                  <div className="flex flex-wrap gap-2">
                    {product.dosages.map((d) => (
                      <button
                        key={d.label}
                        data-testid={`dosage-option-${d.label}`}
                        onClick={() => setDosage(d)}
                        className={`border px-5 py-3 font-mono text-xs transition-colors ${
                          dosage?.label === d.label
                            ? "border-[#00ff72] bg-[#00ff72] text-black"
                            : "border-white/15 text-[#A3A3A3] hover:border-white/40 hover:text-[#F9F6F0]"
                        }`}
                      >
                        {d.label} — ${d.price.toFixed(2)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <div className="flex items-center border border-white/15">
                    <button
                      data-testid="qty-minus-btn"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="px-4 py-4 text-[#A3A3A3] hover:text-[#00ff72]"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span data-testid="qty-value" className="px-4 font-mono text-sm">{qty}</span>
                    <button
                      data-testid="qty-plus-btn"
                      onClick={() => setQty((q) => q + 1)}
                      className="px-4 py-4 text-[#A3A3A3] hover:text-[#00ff72]"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    data-testid="add-to-cart-btn"
                    onClick={handleAdd}
                    disabled={product.sold_out}
                    className={product.sold_out
                      ? "flex flex-1 cursor-not-allowed items-center justify-center gap-3 bg-[#222222] px-8 py-4 font-mono text-xs tracking-[0.2em] text-red-400"
                      : "flex flex-1 items-center justify-center gap-3 bg-[#00ff72] px-8 py-4 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"}
                  >
                    <ShoppingBag className="h-4 w-4" />
                    {product.sold_out ? "SOLD OUT" : `ADD TO CART — $${((dosage?.price || 0) * qty).toFixed(2)}`}
                  </button>
                </div>
              </>
            )}

            <p className="mt-6 border border-red-500/30 bg-red-500/5 p-4 font-mono text-[10px] leading-relaxed tracking-wider text-red-400/90">
              FOR RESEARCH USE ONLY. NOT FOR HUMAN OR VETERINARY CONSUMPTION, DIAGNOSTIC OR
              THERAPEUTIC USE. PURCHASE REQUIRES RESEARCH AFFILIATION.
            </p>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
